import React from "react";
import { isMobile } from "react-device-detect";
import { FormProvider } from "react-hook-form";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import DateChip from "components/DateChip";
import StyledModal from "components/Modal";
import WarningBox from "components/WarningBox";
import StyledFormInput from "components/FormInput/FormInput";
import { AmountTabs } from "pages/CreateInvoice/Form/AmountTabs";
import FormDateTimePicker from "components/FormInput/FormDateTimePicker";
import StyledFormBooleanCheckbox from "components/FormInput/FormBooleanCheckbox";

import PaymentPurposeField from "pages/CreateInvoice/Form/PaymentPurposeField";
import PaymentPurposeOptions from "pages/CreateInvoice/Form/PaymentPurposeOptions";

import { IbanInvoice } from "store/features/ibanInvoiceSlice";

import { useInvoiceUpdateForm } from "./useInvoiceUpdateForm";

import { formatIban } from "shared/functions/formatIban";

import dayjs from "dayjs";

interface InvoiceUpdateModalProps {
  isOpen: boolean;
  invoice?: IbanInvoice | null;
  onAfterSubmit?: () => Promise<void>;
  onClose: () => void;
}

const InvoiceUpdateModal: React.FC<InvoiceUpdateModalProps> = (props) => {
  const { isOpen, invoice, onAfterSubmit, onClose } = props;

  const { methods, onSubmit, onCloseModal } = useInvoiceUpdateForm({
    invoice,
    onAfterSubmit,
    onClose,
  });

  const showNotes = methods.watch("showNotes");
  const showClientNotes = methods.watch("showClientNotes");
  const showExpiration = methods.watch("showInvoiceExpiration");

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isMobile) {
      (e.target as HTMLInputElement).setSelectionRange(0, 0);
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={"Редагувати посилання"}
      content={
        <>
          <WarningBox content="Клієнт уже міг оплатити або почати процес оплати цього посилання. Зміни, які ви вносите, будуть застосовані лише в тому випадку, якщо клієнт ще не почав процес оплати." />
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid #E5E5E5",
              p: "12px 16px",
              m: "16px 0",
            }}
          >
            <DateChip
              date={`Створено ${dayjs
                .utc(invoice?.invoiceDateTimeUtc)
                .local()
                .format("DD MMM YYYY, HH:mm")}`}
            />
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, overflowWrap: "anywhere" }}
              >
                {invoice?.organizationName}
              </Typography>
              <Typography variant="subtitle2" color="grey700">
                {formatIban(invoice?.iban || "")}
              </Typography>
            </Box>
          </Box>
          <FormProvider {...methods}>
            <AmountTabs />
            <PaymentPurposeField />
            <PaymentPurposeOptions />

            <StyledFormBooleanCheckbox
              name="showInvoiceExpiration"
              label="Встановити термін дії посилання"
              sx={{ mb: 0 }}
            />
            <Collapse in={showExpiration}>
              <FormDateTimePicker
                name="expirationDateTimeLocal"
                title="Термін дії"
                selectedSections={isMobile ? null : undefined}
                slotProps={{
                  textField: { onSelect },
                  popper: { placement: "top-start" },
                }}
              />
            </Collapse>
            <StyledFormBooleanCheckbox
              name="showNotes"
              label="Додати нотатку (клієнт її не побачить)"
              sx={{ mb: 0 }}
            />
            <Collapse in={showNotes}>
              <StyledFormInput
                title="Hотатка"
                name="notes"
                multiline
                rows={4}
                fullWidth
                slotProps={{ input: { inputProps: { maxLength: 512 } } }}
              />
            </Collapse>
            <StyledFormBooleanCheckbox
              name="showClientNotes"
              label="Додати нотатку для покупця"
              sx={{ mb: 0 }}
            />
            <Collapse in={showClientNotes}>
              <StyledFormInput
                name="clientNotes"
                multiline
                rows={3}
                fullWidth
                slotProps={{ input: { inputProps: { maxLength: 70 } } }}
              />
            </Collapse>
          </FormProvider>
        </>
      }
      onAction={onSubmit}
    />
  );
};

export default InvoiceUpdateModal;
